"use client";

import { getCurrentDate, type PlainDate } from "@sa-nu/shared-utils/date";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { computeCenteredRangeStart } from "../../reservation-form/_shared/compute-centered-range-start";
import { useAvailabilityStripRangeSize } from "../_hooks/use-availability-strip-range-size";
import { computeAvailabilityStripRange } from "../_utils/compute-availability-strip-range";
import { computeSelectionCenterNight } from "../_utils/compute-selection-center-night";
import { getInitialVisibleDate } from "../_utils/get-initial-visible-date";

import { useRoomTypeReserveContext } from "./room-type-reserve-context";

// reservation-calendar の use-reservation-calendar-date-range.ts と体感を揃えるため同値にする
const DEBOUNCE_MS = 500;

export type AvailabilityStripContextValue = {
  /** 表示中の週の先頭日（即時値）。週の見出し表示に使う */
  rangeStart: PlainDate;
  /** rangeStart を DEBOUNCE_MS 後に反映した値。GraphQL クエリの variables に使い、週送り連打時の発火回数を抑える */
  debouncedRangeStart: PlainDate;
  /** 週送り操作。deltaNights は正負どちらも取り得る */
  shiftRange: (deltaNights: number) => void;
};

const AvailabilityStripContext =
  createContext<AvailabilityStripContextValue | null>(null);

/**
 * 拠点一覧の拠点別カレンダー（AvailabilityStrip）が表示する週を、週送り操作の同期のため拠点カード間でグローバル共有するコンテキスト。
 */
export function AvailabilityStripProvider({
  children,
}: {
  children: ReactNode;
}) {
  // 検索カレンダーと初期表示基準を揃えるため、日程未選択時は今日から1ヶ月先を表示する。
  const [rangeStart, setRangeStart] = useState<PlainDate>(() =>
    getInitialVisibleDate(getCurrentDate()),
  );
  const [debouncedRangeStart, setDebouncedRangeStart] = useState(rangeStart);
  const { rangeSize } = useAvailabilityStripRangeSize();
  const reserveCtx = useRoomTypeReserveContext();
  const firstNight = reserveCtx?.firstNight ?? null;
  const lastNight = reserveCtx?.lastNight ?? null;

  // rangeSize は ref 経由で読むことで、rangeSize の変化では effect を再実行させない
  // （週送り後にユーザーが意図せず表示位置を戻されるのを防ぐ）。
  // ref への代入は render 中に行えない (react-hooks/refs) ため effect 内で毎レンダー後に同期する。
  const rangeSizeRef = useRef(rangeSize);
  useEffect(() => {
    rangeSizeRef.current = rangeSize;
  });

  // 検索条件（日程ピッカー）で firstNight/lastNight が変わったときだけ表示週を追従させる。
  // 連泊選択（firstNight固定でlastNightだけ延長する等）も拾えるよう両方を依存に含める。
  useEffect(() => {
    if (firstNight === null) return;
    // AvailabilityStrip のボーダー表示は CO 日（lastNight+1）まで含むため、中央配置もそれに揃える。
    const centerNight =
      lastNight !== null
        ? computeSelectionCenterNight({
            firstNight,
            lastNight: lastNight.add({ days: 1 }),
          })
        : firstNight;
    const centeredRangeStart = computeCenteredRangeStart({
      centerNight,
      rangeSize: rangeSizeRef.current,
    });
    setRangeStart(centeredRangeStart);
    // 検索条件変更は週送り連打と異なり離散的な操作なので、debounce を待たず即座にクエリへ反映する。
    setDebouncedRangeStart(centeredRangeStart);
  }, [firstNight, lastNight]);

  // 週送りボタン連打による SiteAvailabilityQuery の過剰発火を防ぐため、
  // クエリ用の値は最後の操作から DEBOUNCE_MS 経過後にのみ確定させる。
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedRangeStart(rangeStart);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [rangeStart]);

  const value = useMemo<AvailabilityStripContextValue>(
    () => ({
      rangeStart,
      debouncedRangeStart,
      shiftRange: (deltaNights) =>
        setRangeStart((current) =>
          computeAvailabilityStripRange(current, deltaNights),
        ),
    }),
    [rangeStart, debouncedRangeStart],
  );

  return (
    <AvailabilityStripContext.Provider value={value}>
      {children}
    </AvailabilityStripContext.Provider>
  );
}

export function useAvailabilityStripContext(): AvailabilityStripContextValue {
  const ctx = useContext(AvailabilityStripContext);
  if (ctx === null) {
    throw new Error(
      "useAvailabilityStripContext must be used within AvailabilityStripProvider",
    );
  }
  return ctx;
}
